import * as React from "react";
import { useLocation, Link } from "react-router-dom";
import * as PopoverPrimitive from "@radix-ui/react-popover";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  SymbolIcon,
  Business,
  Zap,
  Money,
  TriangleDown,
  FileIcon,
  PieChart,
  User,
  Tool,
  Settings,
  LogOut,
} from "@/components/icons";
import { useAccountStore } from "@/common/store/accountStore";
import { usePasswordChangeStore } from "@/common/store/passwordChangeStore";
import { usePermission } from "@/hooks/usePermission";
import { canAccessPage } from "@/common/accessControl";
import type { AffiliationType } from "@/api/affiliationType";

// ============================================================================
// 메뉴 데이터 타입 및 정의
// ============================================================================

interface MenuChild {
  label: string;
  path: string;
}

interface MenuItem {
  label: string;
  path?: string;
  icon?: React.ReactNode;
  children?: MenuChild[];
}

const menuItems: MenuItem[] = [
  {
    label: "모니터링",
    icon: <FileIcon size={20} />,
    children: [
      { label: "대시보드", path: "/dashboard" },
      { label: "충전 내역", path: "/charging-history" },
      { label: "충전 인증 내역", path: "/charging-auth-log" },
      { label: "이용 내역", path: "/usage-history" },
      // { label: "결제 내역", path: "/payment" },
    ],
  },
  {
    label: "정산 관리",
    icon: <PieChart size={20} />,
    children: [
      { label: "매출 집계", path: "/revenue" },
      { label: "이용 집계", path: "/usage-summary" },
    ],
  },
  {
    label: "인프라 관리",
    icon: <Zap size={20} />,
    children: [
      { label: "충전소 관리", path: "/station" },
      { label: "충전기 관리", path: "/charger" },
    ],
  },
  {
    label: "로밍 및 요금",
    icon: <Money size={20} />,
    children: [
      { label: "로밍 관리", path: "/roaming" },
      { label: "요금 관리", path: "/price-policy" },
    ],
  },
  {
    label: "고객 관리",
    icon: <User size={20} />,
    children: [
      { label: "운영사 관리", path: "/operator" },
      { label: "고객사 관리", path: "/client" },
      { label: "충전카드 관리", path: "/charger-card" },
      { label: "계정 관리", path: "/account" },
    ],
  },
  {
    label: "운영 관리",
    icon: <Tool size={20} />,
    children: [
      { label: "CS 관리", path: "/cs-inquiry" },
      { label: "유지보수 관리", path: "/maintenance-ticket" },
    ],
  },
];

// 회사 유형 기반 메뉴 노출 필터링. 접근 정책은 accessControl 의 canAccessPage
// 단일 출처를 따른다(라우터 가드와 동일 기준). 직책(MEMBER)은 메뉴를 숨기지 않고
// 쓰기 버튼만 비활성한다.
function filterMenuByPermission(
  items: MenuItem[],
  affiliationType: AffiliationType | null,
): MenuItem[] {
  return items
    .filter((item) => !item.path || canAccessPage(affiliationType, item.path))
    .map((item) => ({
      ...item,
      children: item.children?.filter((child) =>
        canAccessPage(affiliationType, child.path),
      ),
    }))
    .filter((item) => !item.children || item.children.length > 0);
}

// ============================================================================
// NavMain Component - 메인 네비게이션
// ============================================================================

function NavMain({ items }: { items: MenuItem[] }) {
  const location = useLocation();

  // 현재 경로가 해당 메뉴 아이템에 포함되는지 확인.
  // 경로 구분자까지 봐야 한다 — 단순 startsWith 면 /charger-card 에서 /charger 도 켜진다
  const isPathActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);
  const isCategoryActive = (item: MenuItem) =>
    item.children?.some((child) => isPathActive(child.path));

  return (
    <SidebarGroup className="pb-25">
      <SidebarGroupContent>
        <SidebarMenu>
          {items.map((item) => {
            const isActive = isCategoryActive(item);

            return (
              <Collapsible
                key={item.label}
                asChild
                defaultOpen
                className="group/collapsible"
              >
                <SidebarMenuItem>
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      tooltip={item.label}
                      className={cn(
                        "typo-body-2-semibold",
                        isActive
                          ? "bg-bg-alternative text-labels-primary"
                          : "text-labels-alternative hover:bg-fills-primary",
                      )}
                    >
                      {item.icon && (
                        <div
                          className={cn(
                            isActive
                              ? "text-labels-primary"
                              : "text-labels-alternative",
                          )}
                        >
                          {item.icon}
                        </div>
                      )}
                      <span className="typo-body-2-semibold">{item.label}</span>
                      <TriangleDown
                        size={20}
                        className={cn(
                          "ml-auto transition-transform duration-200",
                          "group-data-[state=open]/collapsible:rotate-180",
                        )}
                        color={
                          isActive ? "labels-primary" : "labels-alternative"
                        }
                      />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <SidebarMenuSub className="border-none px-0 pt-4">
                      {item.children?.map((subItem) => {
                        const isSubActive = isPathActive(subItem.path);
                        return (
                          <SidebarMenuSubItem key={subItem.path}>
                            <SidebarMenuSubButton
                              asChild
                              isActive={isSubActive}
                              className={cn(
                                "typo-label-1-medium h-auto py-0",
                                isSubActive
                                  ? "text-labels-primary"
                                  : "text-labels-alternative hover:text-labels-primary hover:bg-transparent",
                              )}
                            >
                              <Link to={subItem.path}>{subItem.label}</Link>
                            </SidebarMenuSubButton>
                          </SidebarMenuSubItem>
                        );
                      })}
                    </SidebarMenuSub>
                  </CollapsibleContent>
                </SidebarMenuItem>
              </Collapsible>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}

// ============================================================================
// NavUser Component - 사용자 정보 영역
// ============================================================================

// 프로필 메뉴 항목 (비밀번호 변경 / 로그아웃)
function ProfileMenuItem({
  icon,
  label,
  onSelect,
  danger,
}: {
  icon: React.ReactNode;
  label: string;
  onSelect: () => void;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        "flex h-8 items-center gap-2 rounded-lg px-2 w-full",
        "typo-label-1-semibold",
        "hover:bg-fills-neutral",
        danger ? "text-status-urgent" : "text-labels-primary",
      )}
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
}

function NavUser() {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { reset, user_info } = useAccountStore();
  const openPasswordModal = usePasswordChangeStore((s) => s.openModal);
  const { isAdmin } = usePermission();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const handleChangePassword = () => {
    setMenuOpen(false);
    openPasswordModal("change");
  };

  const handleLogout = () => {
    setMenuOpen(false);
    // reset() 후 access_token 이 사라지면 PrivateRoute 가 로그인 화면으로 보낸다
    reset();
  };

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <PopoverPrimitive.Root open={menuOpen} onOpenChange={setMenuOpen}>
          <PopoverPrimitive.Trigger asChild>
            {/* 프로필 영역 클릭 시 프로필 메뉴 표시 */}
            <button
              type="button"
              className="flex items-center gap-2 px-2 py-1 w-full rounded-lg hover:bg-fills-neutral"
            >
              {/* 프로필 아바타 */}
              {/* TODO: 비즈니스 로직 - 사용자 프로필 이미지 API 연동 */}
              <div className="size-8 rounded-full bg-fills-neutral flex items-center justify-center shrink-0 inset-border-double">
                <span className="typo-label-2-medium text-labels-alternative">
                  {user_info?.name.charAt(0) ?? "-"}
                </span>
              </div>

              {/* 사용자 정보 (펼침 상태에서만 표시) */}
              {!isCollapsed && (
                <div className="flex flex-col gap-1 flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-1">
                    <span className="typo-label-2-medium text-labels-primary">
                      {user_info?.name ?? "-"}
                    </span>
                    <span className="px-1 py-0.5 rounded bg-primary-normal/10 typo-caption-2-semibold text-primary-normal inset-border-double">
                      {isAdmin ? "관리자 권한" : "읽기 권한"}
                    </span>
                  </div>
                  <span className="typo-caption-1-medium text-labels-assistive truncate">
                    {user_info?.email ?? "-"}
                  </span>
                </div>
              )}
            </button>
          </PopoverPrimitive.Trigger>
          <PopoverPrimitive.Portal>
            <PopoverPrimitive.Content
              side="top"
              align="start"
              sideOffset={8}
              className={cn(
                "z-50 w-50 rounded-xl bg-static-white p-0",
                "inset-border-solid-normal",
                "drop-shadow-[0px_4px_8px_rgba(0,0,0,0.05)]",
                "data-[state=open]:animate-in data-[state=closed]:animate-out",
                "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
              )}
            >
              <div className="flex flex-col gap-1 px-2 py-3">
                <ProfileMenuItem
                  icon={<Settings size={20} color="labels-primary" />}
                  label="비밀번호 변경"
                  onSelect={handleChangePassword}
                />
                <ProfileMenuItem
                  icon={<LogOut size={20} color="status-urgent" />}
                  label="로그아웃"
                  onSelect={handleLogout}
                  danger
                />
              </div>
            </PopoverPrimitive.Content>
          </PopoverPrimitive.Portal>
        </PopoverPrimitive.Root>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}

// ============================================================================
// AppSidebar Component - 메인 사이드바
// ============================================================================

type AppSidebarProps = React.ComponentProps<typeof Sidebar>;

function AppSidebar({ ...props }: AppSidebarProps) {
  const { user_info } = useAccountStore();
  const { affiliationType } = usePermission();
  const visibleMenuItems = filterMenuByPermission(menuItems, affiliationType);

  return (
    <Sidebar
      collapsible="icon"
      className="inset-border-solid-normal-r bg-static-white"
      {...props}
    >
      {/* Header: 로고 및 회사 선택 */}
      <SidebarHeader className="p-4 pt-8 gap-5.75">
        {/* 로고 및 토글 버튼 */}
        <div className="flex items-center justify-between">
          <SymbolIcon className="h-8 w-6.75" />
          <SidebarTrigger className="shadow-none! rounded-none bg-static-white" />
        </div>

        {/* 회사 선택 버튼 */}
        {/* TODO: 비즈니스 로직 - 회사 선택 기능 구현 필요 (다중 회사 지원 시) */}
        <button
          type="button"
          className="flex items-center gap-2.5 w-full px-3 py-1.5 rounded-lg bg-fills-neutral inset-border-double group-data-[collapsible=icon]:hidden"
        >
          <Business size={24} color="labels-primary" />
          <span className="flex-1 text-left typo-label-1-semibold text-labels-primary truncate ">
            {user_info?.affiliation?.name ?? "-"}
          </span>
        </button>
      </SidebarHeader>

      {/* Content: 메뉴 네비게이션 */}
      {/*
        스크롤바(16px)를 오른쪽 패딩 자리에 겹친다.
        패딩과 스크롤바가 나란히 놓이면 우측 여백이 32px로 두 배가 되어 보인다.
        scrollbar-gutter: stable — 스크롤이 없을 때도 같은 16px을 비워 헤더/푸터와 정렬을 맞춘다.
      */}
      <SidebarContent className="group-data-[state=expanded]:pr-0 group-data-[state=expanded]:[scrollbar-gutter:stable]">
        <NavMain items={visibleMenuItems} />
      </SidebarContent>

      {/* Footer: 사용자 정보 */}
      <SidebarFooter className="border-t p-4  group-data-[collapsible=icon]:hidden">
        <NavUser />
      </SidebarFooter>
    </Sidebar>
  );
}

export { AppSidebar };
